import type { ExecAtcSummary, TreeNode } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';

/**
 * 大卡片平面图（原型 v0.22 第 1145–1160 行 `bigCards`）。
 *
 * ## 为什么这一版搬了，而 C-159 那一步没搬
 *
 * C-159 回复以「六个字段库里一个都没有」为由整块未搬。**那句话是核错了。**
 * C-160 A2 逐字段核库（线上 `capsio_wodefu`）的结果是：
 *
 * | 原型字段 | 核的对象 | 态（口径一） |
 * |---|---|---|
 * | 须审批 | `app.atc_version.hotl_gate`（boolean） | **真值** |
 * | 触发方式 | `app.atc_version.trigger_type`（CHECK 四值） | **真值** |
 * | 成熟度 L | `app.atc.maturity_level`（CHECK L0–L5） | **真值** |
 * | desc | 视图 `app.v_atc_one_liner.one_liner` + `missing` | **零态**（视图在，卡的 action_spec 空） |
 * | 均耗 | 视图 `app.v_atc_avg_duration`（源 taskrun） | **零态**（视图在，零执行记录） |
 * | executor | `app.taskrun.actor_type`（CHECK hub/human/hybrid） | **零态**（表在，零执行记录） |
 *
 * **六项无一为无源。** 三项真值、三项零态——按口径二，字段无源不构成整块不搬的理由，
 * 而这里连无源都没有。
 *
 * ## 零态怎么呈现：显示「还没有」，并且说清缺什么
 *
 * - `oneLiner` 拼不出来时，把 `missing` 原样列出来（缺输入源 / 缺 ACTION 主序列 / 缺产出物）
 * - `avgMinutes` 为 null 时写「还没有成功的执行记录」，**不写「—」**：
 *   破折号会被读成「这个数是空的」，而实际含义是「还没跑过」
 * - `actorMix` 三个都是 0 时写「还没有执行记录」，**不推断**这张卡是 AI 还是人工执行
 *
 * ## 一处与原型不同：不补「＋ 规划中 · 待立卡」的空位
 *
 * 原型 `bigCards` 会按 `POSROOMS[p.id]` 补出「FDE 调研已识别，四段与 TEST 待定义」
 * 的空卡。那个数是**演示数据里的一个常量**，库里没有对应的「已识别但未立卡」计数
 * （`proposed` 态的卡是另一回事，它已经在库里了）。补出来就是编一个规划总数。
 * 故只画库里真实存在的卡，`proposed` 态按其真实状态显示。
 */

/** 原型的执行主体标签三档（`exChip`，1141–1147 行）。这里由 `actorMix` 得出，不猜。 */
function ExecutorChip({ mix }: { mix: ExecAtcSummary['actorMix'] }): ReactNode {
  const total = mix.hub + mix.human + mix.hybrid;
  if (total === 0) {
    return <span className="chip">还没有执行记录</span>;
  }
  if (mix.hybrid > 0 || (mix.hub > 0 && mix.human > 0)) {
    return <span className="chip" style={{ color: '#1D5FC2' }}>人机协同</span>;
  }
  if (mix.hub > 0) {
    return <span className="chip" style={{ color: 'var(--hd)' }}>AI 执行</span>;
  }
  return (
    <span
      className="chip"
      style={{ color: '#1D5FC2', borderColor: '#A9C9FB', background: 'var(--blu-bg)' }}
    >
      人工执行
    </span>
  );
}

/**
 * 一个岗位的抬头条（原型 `.posband`）。
 *
 * | 显示 | 来源 |
 * |---|---|
 * | 岗位名、共享岗标记 | 树上的岗位与 `isSystem` |
 * | 人数 | 编制；没录就写「编制未录」，**不折成 0** |
 * | 已立 / 卡位 | `v_position_card_slots`（库算） |
 * | 进入岗位 → | 真跳转 |
 *
 * 原型抬头上还有 AI 替代率与达成率两个百分比。**都不显示**：
 * 前者要编制工时与执行记录，后者没有表、本轮也不建。
 * 不放占位、不放空标记——屏幕上少一个数，比多一个说不清来源的数好。
 */
function PosBand({
  position,
  cardCount,
  headcount,
  onEnter,
}: {
  position: TreeNode;
  cardCount: number;
  headcount: number | null;
  onEnter: () => void;
}): ReactNode {
  const shared = position.attributes['isSystem'] === true;
  const planned = position.attributes['plannedCardCount'];
  return (
    <div className="posband">
      {shared ? '🏛️' : '💼'} {position.name}{' '}
      {shared && <span className="tag sys">共享岗</span>}{' '}
      <span className="m">
        {headcount === null ? '编制未录' : `${headcount} 人`} ·{' '}
        {typeof planned === 'number' ? `${cardCount} / ${planned} 卡位` : `${cardCount} 卡`}
      </span>
      <span style={{ flex: 1 }} />
      <button type="button" className="linkbtn" onClick={onEnter}>
        进入岗位 →
      </button>
    </div>
  );
}

/** 这个岗位还差几间房。卡位总数没填（null）时返回空数组——不画不知道数量的东西。 */
function vacantOf(group: { position: TreeNode; atcs: TreeNode[] }): string[] {
  const planned = group.position.attributes['plannedCardCount'];
  if (typeof planned !== 'number') return [];
  const vacant = Math.max(planned - group.atcs.length, 0);
  return Array.from({ length: vacant }, (_, i) => `vacant-${group.position.id}-${i}`);
}

export function BigCards({
  groups,
  onPick,
  onEnterPosition,
}: {
  /**
   * 按岗位分组的卡（原型是 `d.pos.map(...bigCards(p))`，一个岗位一段）。
   *
   * 上一版是一个平铺的网格——**与原型不是一个结构**，并排图上一眼看得出。
   * T① 的判据是逐屏一致，而「一个岗位一段」正是这一屏的结构。
   */
  groups: { position: TreeNode; headcount: number | null; atcs: TreeNode[] }[];
  onPick: (id: string) => void;
  onEnterPosition: (id: string) => void;
}): ReactNode {
  const atcs = groups.flatMap((g) => g.atcs);
  const [rows, setRows] = useState<ExecAtcSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api.execAtcs().then(
      (data) => live && setRows(data.rows),
      (cause: unknown) => live && setError(messageOf(cause)),
    );
    return () => {
      live = false;
    };
  }, []);

  if (error !== null) return <p className="msg">{error}</p>;
  if (atcs.length === 0) return null;

  const byCode = new Map((rows ?? []).map((r) => [r.code, r]));

  return (
    <div className="card">
      {/* 副标题只说「怎么读这块」，不解释概念。 */}
      <h3>
        任务卡平面图 <span className="sub">一块＝一张 ATC · 点开进卡页</span>
      </h3>

      {groups.map((group) => (
        <div key={group.position.id}>
          <PosBand
            position={group.position}
            cardCount={group.atcs.length}
            headcount={group.headcount}
            onEnter={() => onEnterPosition(group.position.id)}
          />
          <div className="bigrid">
            {group.atcs.map((node) => {
          const row = node.code === null ? undefined : byCode.get(node.code);
          return (
            <button
              type="button"
              className="brm link"
              key={node.id}
              onClick={() => onPick(node.id)}
              title={`${node.name} · 点击打开资产卡片`}
            >
              <span className="dot2" />
              <span className="nm2">{node.name}</span>
              <span className="cd">
                {node.code ?? '（无编码）'}
                {row !== undefined && ` · ${row.status}`}
                {row !== undefined && row.maturityLevel !== '' && ` · ${row.maturityLevel}`}
              </span>
              <span className="ds">
                {/*
                  原型这一行是 `desc`。库里没有 desc 列，一句话说明由
                  `v_atc_one_liner` 从 action_spec 拼；拼不出来就把缺的三样列出来。
                  **不编一句像样的描述填进去**——那是零格冒充真数据（T.2）。
                */}
                {row === undefined
                  ? '读取中……'
                  : (row.oneLiner ??
                    `还拼不出一句话说明，缺：${row.oneLinerMissing.join('、') || '（视图未给出缺项）'}`)}
              </span>
              <span className="ft">
                {row !== undefined && <ExecutorChip mix={row.actorMix} />}
                {row !== undefined && (
                  <span className="chip">
                    {row.avgMinutes === null ? (
                      '均耗：还没有成功的执行记录'
                    ) : (
                      <>
                        <b>均耗 {row.avgMinutes} min</b>/次
                      </>
                    )}
                  </span>
                )}
                {row !== undefined && (
                  <span className="chip">
                    {row.triggerType === null ? '触发方式：这张卡还没有版本' : row.triggerType}
                  </span>
                )}
                {row?.hotlGate === true && (
                  <span className="chip" style={{ color: '#B37A17' }}>
                    须审批
                  </span>
                )}
                {row !== undefined && row.hotlGate === null && (
                  <span className="chip">须审批：这张卡还没有版本</span>
                )}
              </span>
              </button>
              );
            })}

            {/*
              空卡位，形态照原型「＋ 规划中 · 待立卡」。
              **空房间是蓝图的主体，不是缺陷**——画出来的是「这一层还有几间要开」。
              卡位总数没填时不画：那时连差几间都还不知道。
            */}
            {vacantOf(group).map((key) => (
              <div className="brm plan" key={key}>
                <span>＋ 规划中 · 待立卡</span>
                <span style={{ fontSize: 10.5 }}>待定义四段与判据</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="note" style={{ marginTop: 10 }}>
        每一块的状态、成熟度、触发方式、须审批取自这张卡自己；
        一句话说明由卡的四段拼出来，四段没填就写「还没填」。
        虚线的那几块是空卡位——这个岗位规划了、还没立的那几张。
      </div>
    </div>
  );
}
